import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    Typography,
    alpha,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    PLATFORM_INFO,
} from "../../../api/services/socialMediaService";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

const premiumCard = {
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
    overflow: "hidden",
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const postStatusColors: Record<string, string> = {
    draft: "#94A3B8",
    approved: "#16A34A",
    scheduled: "#2563EB",
    queued: "#EA580C",
    posting: "#2563EB",
    posted: "#16A34A",
    failed: "#DC2626",
};

const SocialCalendar = () => {
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState<"month" | "week">("month");

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Calculate date range for calendar
    const start = useMemo(() => {
        const d = new Date(year, month, 1);
        d.setDate(d.getDate() - d.getDay());
        return d.toISOString().split("T")[0];
    }, [year, month]);

    const end = useMemo(() => {
        const d = new Date(year, month + 1, 0);
        d.setDate(d.getDate() + (6 - d.getDay()));
        return d.toISOString().split("T")[0];
    }, [year, month]);

    const { data, isLoading } = useQuery(
        ["social-calendar", start, end],
        () => socialMediaService.getCalendar(start, end)
    );

    const calendarData: Record<string, any[]> = data?.data || {};

    // Generate calendar grid
    const calendarDays = useMemo(() => {
        const days: Date[] = [];
        const firstDay = new Date(year, month, 1);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - startDate.getDay());

        for (let i = 0; i < 42; i++) {
            const d = new Date(startDate);
            d.setDate(d.getDate() + i);
            days.push(d);
        }
        return days;
    }, [year, month]);

    const navigateMonth = (delta: number) => {
        setCurrentDate(new Date(year, month + delta, 1));
    };

    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    if (isLoading) return <Loader />;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                        Content Calendar
                    </Typography>
                    <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                        Schedule and track your social media posts
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => navigateMonth(-1)}>
                        <ChevronLeftRoundedIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: "18px", minWidth: 180, textAlign: "center" }}>
                        {MONTHS[month]} {year}
                    </Typography>
                    <IconButton onClick={() => navigateMonth(1)}>
                        <ChevronRightRoundedIcon />
                    </IconButton>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCurrentDate(new Date())}
                        sx={{ ml: 1 }}
                    >
                        Today
                    </Button>
                </Box>
            </Box>

            {/* Calendar Grid */}
            <Card sx={premiumCard}>
                {/* Day Headers */}
                <Grid container sx={{ borderBottom: "1px solid rgba(226,232,240,0.5)" }}>
                    {DAYS.map((day) => (
                        <Grid
                            item
                            key={day}
                            xs={12 / 7}
                            sx={{
                                py: 1.5,
                                textAlign: "center",
                                backgroundColor: "#F8FAFC",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#64748B",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                }}
                            >
                                {day}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                {/* Calendar Cells */}
                {Array.from({ length: 6 }, (_, weekIdx) => (
                    <Grid
                        container
                        key={weekIdx}
                        sx={{ borderBottom: "1px solid rgba(226,232,240,0.3)" }}
                    >
                        {calendarDays.slice(weekIdx * 7, weekIdx * 7 + 7).map((day) => {
                            const dateStr = day.toISOString().split("T")[0];
                            const posts = calendarData[dateStr] || [];
                            const isCurrentMonth = day.getMonth() === month;
                            const isToday = dateStr === todayStr;

                            return (
                                <Grid
                                    item
                                    key={dateStr}
                                    xs={12 / 7}
                                    sx={{
                                        minHeight: 100,
                                        p: 1,
                                        borderRight: "1px solid rgba(226,232,240,0.3)",
                                        backgroundColor: isToday
                                            ? alpha("#00897B", 0.04)
                                            : isCurrentMonth
                                                ? "#FFFFFF"
                                                : "#FAFBFC",
                                        "&:last-child": { borderRight: "none" },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: isToday ? 800 : 600,
                                            color: isToday
                                                ? "#00897B"
                                                : isCurrentMonth
                                                    ? "#1E293B"
                                                    : "#CBD5E1",
                                            mb: 0.5,
                                        }}
                                    >
                                        {day.getDate()}
                                    </Typography>

                                    {/* Post indicators */}
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        {posts.slice(0, 3).map((post: any, i: number) => {
                                            const info = PLATFORM_INFO[post.platform?.platform] || {
                                                name: "?",
                                                color: "#666",
                                                icon: "?",
                                            };
                                            const statusColor =
                                                postStatusColors[post.status] || "#94A3B8";

                                            return (
                                                <Box
                                                    key={post.id || i}
                                                    onClick={() =>
                                                        post.campaign_id &&
                                                        navigate(
                                                            `/social-media/campaigns/${post.campaign_id}`
                                                        )
                                                    }
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 0.75,
                                                        py: 0.25,
                                                        borderRadius: "4px",
                                                        backgroundColor: alpha(info.color, 0.08),
                                                        cursor: "pointer",
                                                        "&:hover": {
                                                            backgroundColor: alpha(info.color, 0.15),
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: "50%",
                                                            backgroundColor: statusColor,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            color: "#475569",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {info.icon}{" "}
                                                        {post.content?.substring(0, 20) || "Post"}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                        {posts.length > 3 && (
                                            <Typography
                                                sx={{
                                                    fontSize: "10px",
                                                    color: "#94A3B8",
                                                    fontWeight: 600,
                                                    pl: 0.75,
                                                }}
                                            >
                                                +{posts.length - 3} more
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                ))}
            </Card>

            {/* Legend */}
            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                {Object.entries(postStatusColors).map(([status, color]) => (
                    <Box key={status} display="flex" alignItems="center" gap={0.5}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: color,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: "11px",
                                color: "#64748B",
                                textTransform: "capitalize",
                            }}
                        >
                            {status}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default SocialCalendar;
